import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type {
  PassportComponentCard,
  PassportLedgerView,
  PassportResultItem,
  PassportRecResult,
} from '@siliconx/shared';
import { Card } from '@siliconx/shared/ui/Card';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { formatTs } from '../../lib/format';

/**
 * SIL-2373 (Bench R2 P3-02): the ledger passport sections (design section 11, D3, D11, D12), rendered from
 * GET /api/client/passport/:fingerprint/ledger. The owner's org gets the owner view; anyone else gets a 404 here (the
 * masked buyer view is the public QR page, not this route). This component draws only what the DTO carries and never asks for FMV or raw files. Every status is stated as a
 * result, never as certification (D3). While the buyer switch is OFF the route 404s for non-owners and the
 * section renders nothing.
 */

interface ApiError extends Error {
  status?: number;
}

export function useLedgerPassportQuery(fingerprint: string) {
  return useQuery<PassportLedgerView, ApiError>({
    queryKey: qk.assets.passportLedger(fingerprint),
    queryFn: async () => {
      const res = await api.api.client.passport[':fingerprint'].ledger.$get({ param: { fingerprint } });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status });
      }
      const body = (await res.json()) as unknown as { ledger: PassportLedgerView };
      return body.ledger;
    },
    retry: false,
  });
}

const VERDICT_TEXT: Record<PassportResultItem['verdict'], string> = {
  pass: 'Passed',
  fail: 'Failed',
  incomplete: 'Incomplete',
  not_run: 'Not run',
};

const VERDICT_TONE: Record<PassportResultItem['verdict'], string> = {
  pass: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  fail: 'bg-red-50 text-red-800 border-red-200',
  incomplete: 'bg-amber-50 text-amber-800 border-amber-200',
  not_run: 'bg-zinc-100 text-zinc-700 border-zinc-200',
};

const NEUTRAL_TONE = 'bg-zinc-100 text-zinc-700 border-zinc-200';
const WARN_TONE = 'bg-amber-50 text-amber-800 border-amber-200';

function Chip({ tone, children }: { tone: string; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${tone}`}>
      {children}
    </span>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return <h2 className="text-sm font-semibold text-foreground">{children}</h2>;
}

function functionalText(rec: PassportRecResult): string {
  if (rec.category) return rec.categoryLabel ? `${rec.category} - ${rec.categoryLabel}` : rec.category;
  return rec.reasonText ?? 'No functional category recorded';
}

export function RecStatusRow({ header }: { header: PassportLedgerView['header'] }) {
  const { data, functional, cosmetic } = header.recStatus;
  return (
    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="rec-status">
      <div>
        <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Data status</dt>
        <dd className="mt-1 text-sm text-foreground">{data.label}</dd>
      </div>
      <div>
        <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Functional</dt>
        <dd className="mt-1 text-sm text-foreground">{functionalText(functional)}</dd>
        {functional.cappedByRequiredClass && functional.missingClasses.length > 0 ? (
          <dd className="mt-1 text-xs text-muted-foreground">
            No complete result for: {functional.missingClasses.join(', ')}
          </dd>
        ) : null}
      </div>
      <div>
        <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Cosmetic</dt>
        <dd className="mt-1 text-sm text-foreground">{cosmetic.code ?? 'Not recorded'}</dd>
      </div>
    </dl>
  );
}

function ComponentCard({ card }: { card: PassportComponentCard }) {
  return (
    <div className="rounded-lg border border-border p-3 text-sm" data-testid="component-card">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="font-medium text-foreground">{card.label}</div>
          {card.modelName ? <div className="text-xs text-muted-foreground">{card.modelName}</div> : null}
          {card.identifier ? (
            <div className="font-num text-xs text-muted-foreground">{card.identifier}</div>
          ) : card.maskedId ? (
            <div className="font-num text-xs text-muted-foreground">{card.maskedId}</div>
          ) : null}
        </div>
        <Chip tone={card.rec.category ? NEUTRAL_TONE : WARN_TONE}>{functionalText(card.rec)}</Chip>
      </div>
      {card.functions.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {card.functions.map((f) => (
            <li key={f.functionId}>
              <Chip
                tone={
                  f.status === 'pass' ? VERDICT_TONE.pass : f.status === 'fail' ? VERDICT_TONE.fail : VERDICT_TONE.incomplete
                }
              >
                {f.functionId}: {f.status === 'missing' ? 'no complete result' : f.status === 'pass' ? 'passed' : 'failed'}
                {f.key ? ' (key)' : ''}
              </Chip>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="mt-2 text-xs">
        {card.link.state === 'linked' ? (
          <Link
            to="/passport/$fingerprint"
            params={{ fingerprint: card.link.fingerprint }}
            className="font-medium text-foreground hover:underline"
          >
            {card.link.label} &rarr;
          </Link>
        ) : (
          <span className="text-muted-foreground">{card.link.label}</span>
        )}
      </div>
    </div>
  );
}

/** Position of a numeric value on the drawn track (0..1). The track spans the limit range plus 25% headroom each side. */
export function gaugePosition(value: number, min: number | null, max: number | null): number {
  const lo = min ?? Math.min(0, value);
  const hi = max ?? Math.max(value, lo + 1);
  const span = hi - lo;
  if (span <= 0) return 0.5;
  const pad = span * 0.25;
  const pos = (value - (lo - pad)) / (span + 2 * pad);
  return Math.min(1, Math.max(0, pos));
}

function LimitGauge({
  name,
  value,
  unit,
  min,
  max,
}: {
  name: string;
  value: number;
  unit: string;
  min: number | null;
  max: number | null;
}) {
  const lo = min ?? Math.min(0, value);
  const hi = max ?? Math.max(value, lo + 1);
  const pad = (hi - lo) * 0.25;
  const total = hi - lo + 2 * pad;
  const bandLeft = total > 0 ? (pad / total) * 100 : 0;
  const bandWidth = total > 0 ? ((hi - lo) / total) * 100 : 100;
  const pos = gaugePosition(value, min, max) * 100;
  return (
    <div className="space-y-1" data-testid="limit-gauge">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{name}</span>
        <span className="font-num text-foreground">
          {value} {unit}
        </span>
      </div>
      <div
        className="relative h-2 rounded-full bg-zinc-100"
        role="img"
        aria-label={`${name} ${value} ${unit}, limits ${min ?? 'none'} to ${max ?? 'none'}`}
      >
        <div
          className="absolute inset-y-0 rounded-full bg-emerald-200"
          style={{ left: `${bandLeft}%`, width: `${bandWidth}%` }}
        />
        <div className="absolute top-[-2px] h-3 w-1 rounded bg-violet-600" style={{ left: `${pos}%` }} />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground font-num">
        <span>{min ?? ''}</span>
        <span>{max ?? ''}</span>
      </div>
    </div>
  );
}

function parseNum(s: string | null): number | null {
  if (s === null || s.trim() === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function ResultCard({ result, label }: { result: PassportResultItem; label: string }) {
  const gauges = result.measurements.flatMap((m) => {
    const value = parseNum(m.value);
    const lim = result.limits.find((l) => l.name === m.name);
    if (value === null || !lim) return [];
    const min = parseNum(lim.min);
    const max = parseNum(lim.max);
    if (min === null && max === null) return [];
    return [{ name: m.name, value, unit: m.unit, min, max }];
  });
  return (
    <li className="rounded-lg border border-border p-3 text-sm" data-testid="result-item">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <span className="font-medium text-foreground">{result.functionId}</span>
          <span className="text-muted-foreground"> - {label}</span>
        </div>
        <div className="flex items-center gap-2">
          <Chip tone={VERDICT_TONE[result.verdict]}>{VERDICT_TEXT[result.verdict]}</Chip>
          <span className="text-xs text-muted-foreground">{formatTs(result.at)}</span>
        </div>
      </div>
      {result.measurements.length > 0 ? (
        <table className="mt-2 w-full text-xs">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="font-medium">Measurement</th>
              <th className="font-medium">Value</th>
              <th className="font-medium">Limit</th>
            </tr>
          </thead>
          <tbody>
            {result.measurements.map((m) => {
              const lim = result.limits.find((l) => l.name === m.name);
              const limText = lim
                ? [lim.min !== null ? `min ${lim.min}` : null, lim.max !== null ? `max ${lim.max}` : null]
                    .filter(Boolean)
                    .join(', ')
                : '';
              return (
                <tr key={m.name} className="border-t border-border">
                  <td className="py-1 text-foreground">{m.name}</td>
                  <td className="py-1 font-num">
                    {m.value} {m.unit}
                  </td>
                  <td className="py-1 font-num text-muted-foreground">{limText || '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
      {gauges.length > 0 ? (
        <div className="mt-3 space-y-2">
          {gauges.map((g) => (
            <LimitGauge key={g.name} {...g} />
          ))}
        </div>
      ) : null}
      {result.tool ? (
        <div className="mt-2 text-xs text-muted-foreground">
          Tool: {result.tool}
          {result.toolVersion ? ` ${result.toolVersion}` : ''}
          {result.durationSeconds !== null ? ` - ${result.durationSeconds}s` : ''}
        </div>
      ) : null}
    </li>
  );
}

export function LedgerPassportBody({ ledger }: { ledger: PassportLedgerView }) {
  const cardLabel = new Map<string, string>();
  for (const g of ledger.componentGroups) for (const c of g.components) cardLabel.set(c.key, c.label);
  const isOwner = ledger.audience === 'owner';
  const noEntries = ledger.evidence.ledger === 'no_entries';

  return (
    <div className="space-y-6" data-testid="ledger-passport" data-audience={ledger.audience}>
      <Card className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <SectionTitle>Test results and record status</SectionTitle>
            <p className="text-sm text-muted-foreground">
              {[ledger.header.manufacturerName, ledger.header.modelName].filter(Boolean).join(' ') ||
                (ledger.subject === 'component' ? 'Component' : 'Unit')}
              {ledger.header.maskedId ? <span className="font-num"> {ledger.header.maskedId}</span> : null}
            </p>
            {isOwner && ledger.header.serialNumber ? (
              <p className="font-num text-xs text-muted-foreground">Serial {ledger.header.serialNumber}</p>
            ) : null}
          </div>
          <Chip tone={NEUTRAL_TONE}>{ledger.subject === 'unit' ? 'Unit passport' : 'Component passport'}</Chip>
        </div>
        {noEntries ? (
          <p className="text-sm text-muted-foreground" data-testid="not-evaluated">
            Not evaluated. No ledger entries are recorded for this asset yet.
          </p>
        ) : (
          <RecStatusRow header={ledger.header} />
        )}
        {ledger.links.unit ? (
          <Link
            to="/passport/$fingerprint"
            params={{ fingerprint: ledger.links.unit.fingerprint }}
            className="text-sm font-medium text-foreground hover:underline"
          >
            Unit passport &rarr;
          </Link>
        ) : null}
      </Card>

      {ledger.componentGroups.map((g) => (
        <Card key={g.key} className="space-y-3">
          <SectionTitle>{g.label}</SectionTitle>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {g.components.map((c) => (
              <ComponentCard key={c.key} card={c} />
            ))}
          </div>
        </Card>
      ))}

      {ledger.results.length > 0 ? (
        <Card className="space-y-3">
          <SectionTitle>Latest result per function</SectionTitle>
          <ul className="space-y-3">
            {ledger.results.map((r) => (
              <ResultCard
                key={r.entryId}
                result={r}
                label={r.componentKey ? (cardLabel.get(r.componentKey) ?? 'Component') : 'Unit'}
              />
            ))}
          </ul>
        </Card>
      ) : null}

      {ledger.configChanges.length > 0 ? (
        <Card className="space-y-3">
          <SectionTitle>Configuration changes</SectionTitle>
          <ul className="space-y-1 text-sm" data-testid="config-changes">
            {ledger.configChanges.map((c) => (
              <li key={c.entryId} className="flex flex-wrap gap-2">
                <span className="text-foreground">
                  {c.kind} {c.change}
                </span>
                {c.maskedId ? <span className="font-num text-muted-foreground">{c.maskedId}</span> : null}
                <span className="text-muted-foreground">{formatTs(c.changedOn)}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {ledger.history.length > 0 ? (
        <Card className="space-y-3">
          <SectionTitle>History</SectionTitle>
          <ol className="space-y-1 text-sm" data-testid="history">
            {ledger.history.map((h) => (
              <li key={h.entryId} className="flex flex-wrap gap-2">
                <span className="text-xs text-muted-foreground font-num">{formatTs(h.at)}</span>
                <span className="text-foreground">{h.summary}</span>
                {h.superseded ? <Chip tone={NEUTRAL_TONE}>Corrected by a later entry</Chip> : null}
              </li>
            ))}
          </ol>
        </Card>
      ) : null}

      <Card className="space-y-3">
        <SectionTitle>Evidence</SectionTitle>
        <p className="text-xs text-muted-foreground">
          {ledger.evidence.signing.signedEntries} of {ledger.evidence.signing.totalEntries} entries carry a signature.{' '}
          {ledger.evidence.signing.note}
        </p>
        {ledger.evidence.entries.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="evidence-entries">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="font-medium">#</th>
                  <th className="font-medium">Type</th>
                  <th className="font-medium">Entry hash</th>
                  <th className="font-medium">Signature</th>
                </tr>
              </thead>
              <tbody>
                {ledger.evidence.entries.map((e) => (
                  <tr key={e.entryId} className="border-t border-border">
                    <td className="py-1 font-num">{e.seq}</td>
                    <td className="py-1 text-foreground">{e.type}</td>
                    <td className="py-1 font-num break-all text-muted-foreground">{e.entryHash}</td>
                    <td className="py-1">{e.signature.state === 'signed' ? 'Signed' : 'Unsigned'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {isOwner && ledger.evidence.files && ledger.evidence.files.length > 0 ? (
          <div data-testid="evidence-files">
            <div className="text-xs font-medium text-foreground">Evidence files (metadata)</div>
            <ul className="mt-1 space-y-1 text-xs">
              {ledger.evidence.files.map((f) => (
                <li key={`${f.entryId}-${f.sha256}`} className="font-num text-muted-foreground break-all">
                  {f.kind} - {f.sizeBytes} bytes - {f.sha256}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Card>

      <p className="text-xs text-muted-foreground">{ledger.footer}</p>
    </div>
  );
}

/** Renders nothing while loading, on 404 (buyer view not enabled / not available) or on any error: the existing passport stays. */
export function LedgerPassport({ fingerprint }: { fingerprint: string }) {
  const q = useLedgerPassportQuery(fingerprint);
  if (!q.data) return null;
  return <LedgerPassportBody ledger={q.data} />;
}
